import React, { useEffect, forwardRef, useImperativeHandle, useState } from 'react';
import { Form, Input, Select, Spin, Row, Col } from 'antd';
import s from './s.module.less';
import pageS from '../../s.module.less';
import { PayoutAccount, PayoutAccountType } from 'types/paymentSetup';
import { usePayoutAccount } from 'hooks/paymentSetup/usePayoutAccount';
import { editPayoutAccount } from 'api/paymentSetup';
import stripeLogo from 'assets/logo/stripeLogo.png';

const { Option } = Select;

const accountTypeOptions = [
    { value: PayoutAccountType.CHECKING, label: 'Checking account' },
    { value: PayoutAccountType.SAVINGS, label: 'Saving account' },
    { value: PayoutAccountType.FUTSU, label: 'Futsu account' },
    { value: PayoutAccountType.TOZA, label: 'Toza account' },
];

interface PayoutAccountStepProps {
    isEdit?: boolean;
    isReview?: boolean;
}

export interface PayoutAccountStepHandles {
    onSubmit: () => Promise<void>;
}

const SummaryItem = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <div className={s.summaryItem}>
        <div className={s.summaryLabel}>{label}</div>
        <div className={s.summaryValue}>{value || '-'}</div>
    </div>
);

const PayoutAccountStep = forwardRef<PayoutAccountStepHandles, PayoutAccountStepProps>(({
    isEdit = true,
    isReview = false,
}, ref) => {
    const [form] = Form.useForm<PayoutAccount>();
    const [apiFormData, loading, fetchData] = usePayoutAccount();

    const [summaryFirstName, setSummaryFirstName] = useState<string>('');
    const [summaryLastName, setSummaryLastName] = useState<string>('');
    const [summaryBankName, setSummaryBankName] = useState<string>('');
    const [summaryAccountTypeLabel, setSummaryAccountTypeLabel] = useState<string>('');
    const [summaryRouting, setSummaryRouting] = useState<string>('-');
    const [summaryAccount, setSummaryAccount] = useState<string>('-');

    const updateSummaryState = (values: Partial<PayoutAccount>) => {
        setSummaryFirstName(values.firstName || '');
        setSummaryLastName(values.lastName || '');
        setSummaryBankName(values.bankName || '');
        setSummaryAccountTypeLabel(accountTypeOptions.find((opt) => opt.value === values.accountType)?.label || '');
        setSummaryRouting(values.routingNumber || '');
        setSummaryAccount(values.accountNumber || '');
    };

    useEffect(() => {
        if (apiFormData) {
            const initialValues = {
                ...apiFormData,
                confirmAccountNumber: apiFormData.accountNumber,
            };
            form.setFieldsValue(initialValues);
            updateSummaryState(initialValues);
        }
    }, [apiFormData, form]);

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            await editPayoutAccount({
                ...values,
                accountType: values.accountType ? values.accountType : null,
            });
            await fetchData();
        } catch (errorInfo) {
            console.error('Payout Account validation/save failed:', errorInfo);
            throw errorInfo;
        }
    };

    useImperativeHandle(ref, () => ({
        onSubmit: handleSubmit,
    }));

    const renderSummaryView = () => {
        return (
            <div className={s.summaryContainer}>
                <Row gutter={[24, 32]}>
                    <Col span={12}><SummaryItem label="First name" value={summaryFirstName} /></Col>
                    <Col span={12}><SummaryItem label="Last name" value={summaryLastName} /></Col>

                    <Col span={12}><SummaryItem label="Bank name" value={summaryBankName} /></Col>
                    <Col span={12}><SummaryItem label="Account type" value={summaryAccountTypeLabel} /></Col>

                    <Col span={24}><SummaryItem label="Routing number" value={summaryRouting} /></Col>

                    <Col span={12}><SummaryItem label="Account number" value={summaryAccount} /></Col>
                    <Col span={12} />
                </Row>
            </div>
        );
    };

    return (
        <Spin spinning={loading}>
            <div className={s.payoutAccountStepContainer}>
                {!isReview ? (
                    <div className={s.header}>
                        <h2 className={pageS.stepTitle}>Payout account</h2>
                        <p className={pageS.desc}>
                            Add your bank info to receive payments from patients.
                        </p>
                        <img className={s.stripeLogo} src={stripeLogo} alt="stripe logo" />
                    </div>
                ) : null}

                {!isReview ? (
                    <>
                        <h3 className={pageS.title}>Set up your bank account</h3>
                        <p className={pageS.desc}>
                            All patient fees including no-show, late cancellation, and rescheduling charges will be deposited to this account.
                        </p>
                    </>
                ) : null}

                {!isEdit ? (
                    renderSummaryView()
                ) : (
                    <div className={pageS.card} style={{ marginTop: 16 }}>
                        <Form form={form} layout="vertical" className={s.payoutForm}>
                            <h4 className={s.subHeader}>Account holder details</h4>
                            <div className={s.formRow}>
                                <Form.Item
                                    label="First name"
                                    name="firstName"
                                    className={s.formCol}
                                >
                                    <Input placeholder="First name" />
                                </Form.Item>
                                <Form.Item
                                    label="Middle name"
                                    name="middleName"
                                    className={s.formCol}
                                >
                                    <Input placeholder="Middle name" />
                                </Form.Item>
                                <Form.Item
                                    label="Last name"
                                    name="lastName"
                                    className={s.formCol}
                                >
                                    <Input placeholder="Last name" />
                                </Form.Item>
                            </div>

                            <div className={s.formRow}>
                                <Form.Item
                                    label="Bank name"
                                    name="bankName"
                                    className={s.formCol}
                                >
                                    <Input placeholder="Enter a bank name" />
                                </Form.Item>
                                <Form.Item
                                    label="Account type"
                                    name="accountType"
                                    className={s.formCol}
                                    rules={[
                                        ({ getFieldValue }) => ({
                                            validator(_, value) {
                                                const hasRoutingNumber = getFieldValue('routingNumber');
                                                const hasAccountNumber = getFieldValue('accountNumber');

                                                if ((hasRoutingNumber || hasAccountNumber) && !value) {
                                                    return Promise.reject(new Error('Please select an account type'));
                                                }
                                                return Promise.resolve();
                                            },
                                        }),
                                    ]}
                                >
                                    <Select placeholder="Select an account type">
                                        {accountTypeOptions.map((opt) => (
                                            <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </div>

                            <Form.Item
                                label="Routing number"
                                name="routingNumber"
                                rules={[
                                    {
                                        validator: (_, value) => {
                                            if (!value) {
                                                return Promise.resolve();
                                            }
                                            if (/^\d{9}$/.test(value)) {
                                                return Promise.resolve();
                                            }
                                            return Promise.reject(new Error('Please enter a valid 9-digit routing number'));
                                        },
                                    },
                                ]}
                            >
                                <Input placeholder="Enter 9 digit routing number" maxLength={9} />
                            </Form.Item>

                            <div className={s.formRow}>
                                <Form.Item
                                    label="Account number"
                                    name="accountNumber"
                                    className={s.formCol}
                                    rules={[
                                        {
                                            validator: (_, value) => {
                                                if (!value) {
                                                    return Promise.resolve();
                                                }
                                                if (/^\d+$/.test(value)) {
                                                    return Promise.resolve();
                                                }
                                                return Promise.reject(new Error('Please enter a valid account number'));
                                            },
                                        },
                                    ]}
                                >
                                    <Input placeholder="Enter bank account number" />
                                </Form.Item>
                                <Form.Item
                                    label="Confirm account number"
                                    name="confirmAccountNumber"
                                    dependencies={['accountNumber']}
                                    hasFeedback
                                    rules={[
                                        ({ getFieldValue }) => ({
                                            validator(_, value) {
                                                if (!value || getFieldValue('accountNumber') === value) {
                                                    return Promise.resolve();
                                                }
                                                if (!/^\d+$/.test(value)) {
                                                    return Promise.reject(new Error('Please enter a valid account number'));
                                                }
                                                if (getFieldValue('accountNumber') !== value) {
                                                    return Promise.reject(new Error('The two account numbers do not match!'));
                                                }
                                                return Promise.resolve();
                                            },
                                        }),
                                    ]}
                                    className={s.formCol}
                                >
                                    <Input placeholder="Re-enter bank account number" />
                                </Form.Item>
                            </div>
                        </Form>
                    </div>
                )}
            </div>
        </Spin>
    );
});

export default PayoutAccountStep;
